import { Box } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import StopOutlinedIcon from "@mui/icons-material/StopOutlined";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import HandshakeIcon from "@mui/icons-material/Handshake";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import PersonOffIcon from "@mui/icons-material/PersonOff";
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import { Icon } from "lucide-react";
import { Remove, ToggleOffOutlined, ToggleOnOutlined, Verified } from "@mui/icons-material";

const SUCCESS_GREEN = "#276D20";

const greenTheme = {
  bg: "rgba(233, 240, 233, 1)",
  color: SUCCESS_GREEN,
  iconBg: SUCCESS_GREEN,
  Icon: CheckIcon,
};

const blueTheme = {
  bg: "rgba(0, 111, 230, .12)",
  color: "#006FE6",
  iconBg: "#006FE6",
  Icon: CheckIcon,
};

const grayTheme = {
  bg: "rgba(103, 103, 103, .12)",
  color: "#676767",
  iconBg: "#676767",
  Icon: CloseIcon,
  border: "1px solid #676767",
};

const redTheme = {
  bg: "rgba(227,26,28,0.12)",
  color: "#E31A1C",
  iconBg: "#E31A1C",
  Icon: CloseIcon,
};

const orangeTheme = {
  bg: "rgba(255, 193, 7, 0.12)",
  color: "#FF8F00",
  iconBg: "#FF8F00",
  Icon: HourglassEmptyIcon,
};

const STATUS_THEME = {
  active: {
    ...greenTheme,
    Icon: CheckIcon,
  },
  successful: greenTheme,
  success: greenTheme,
  approved: greenTheme,
  accepted: greenTheme,
  validated: greenTheme,
  assigned: greenTheme,
  activate: greenTheme,
  grant: grayTheme,
  charge: grayTheme,
  usage: grayTheme,
  closed: redTheme,
  deactivate: redTheme,
  true: greenTheme,
  false: redTheme,
  ready: blueTheme,
  
  // Inactive statuses
  inactive: {
    ...redTheme,
    Icon: CloseIcon,
  },
  
  // Pending/Unverified statuses
  pending: {
    bg: "rgba(255,193,7,.12)",
    color: "#FFC107",
    iconBg: "#FFC107",
    Icon: HourglassEmptyIcon,
  },
  unverified: {
    ...orangeTheme,
    Icon: HourglassEmptyIcon,
  },
  unassigned: {
    ...orangeTheme,
    Icon: PriorityHighIcon,
  },

  terminated: {
    ...redTheme,
    Icon: StopOutlinedIcon
  },

  enabled: {
    ...greenTheme,
    Icon: Verified
  },

  disabled: {
    ...redTheme,
    Icon: Remove
  },
  
  // Failed/Error statuses
  failed: redTheme,
  cancel: redTheme,
  
  // Unregistered status
  unregistered: {
    bg: "#F3B86033",
    color: "#FF9900",
    iconBg: "#FF9900",
    Icon: PersonOffIcon,
  },
  
  // Other statuses
  completed: greenTheme,
  acknowledged: {
    color: "rgba(255, 144, 0, 1)",
    bg: "rgba(255, 242, 200, 1)",
    iconBg: "rgba(255, 144, 0, 1)",
    Icon: HandshakeIcon
  },
  "in progress": {
    ...orangeTheme,
    Icon:HourglassEmptyIcon
  },
};

const StatusBadge = ({ value, customTheme, isCustomerTag = false }) => {
  if (!value || value === '-') {
    return (
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          px: 1.5,
          py: 0.5,
          borderRadius: 1,
          backgroundColor: grayTheme.bg,
          color: grayTheme.color,
          fontSize: "0.875rem",
          fontWeight: 500,
          border: grayTheme.border,
        }}
      >
        -
      </Box>
    );
  }

  const key = value.toString().toLowerCase().trim();
  
  // Use custom theme if provided, otherwise fall back to STATUS_THEME or generalTheme
  const theme = isCustomerTag ? blueTheme : customTheme || STATUS_THEME[key] || grayTheme;
  const { 
    bg, 
    color, 
    iconBg, 
    Icon, 
    border, 
    px = 1.5, 
    py = 0.5, 
    borderRadius = 1
  } = theme;

  // Capitalize the first letter for display
  // const displayValue = value?.charAt(0)?.toUpperCase() + value?.slice(1)?.toLowerCase(); 

  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 1,
        px: px,
        py: py,
        borderRadius: borderRadius,
        backgroundColor: bg,
        color: color,
        fontSize: "0.875rem",
        fontWeight: 500,
        border: border || `1px solid ${color}20`,
      }}
    >
      {Icon && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 16,
            height: 16,
            borderRadius: "50%",
            backgroundColor: iconBg,
            color: "white",
          }}
        >
          <Icon sx={{ fontSize: 12 }} />
        </Box>
      )}
      <span className="text-capitalize">{value}</span>
      {/* {displayValue} */}
    </Box>
  );
};

export default StatusBadge;